import * as React from "react";

import { DatasheetItem } from "@modules/datasheets/components/datasheet-item";
import { DatasheetItemLabel } from "@modules/datasheets/components/datasheet-item-label";
import { CheckboxFieldProps, useCheckboxFieldController } from "./checkbox-field.controller";
import { getHeadingAlignmentStyle, getHeadingPositionOffset } from "../field-heading.utils";
import { Flex } from "@common/components/Flex";
import { Checkbox } from "@common/components/checkbox";

import { FieldContainer } from "../field-container";
import { Hint } from "@common/components/hint";
import { useDatasheetNavigationContext, useDatasheetResultFieldEditable, useForceDisabled } from "../../context";

import "./checkbox-field.styles.scss";

export const CheckboxField: React.FC<CheckboxFieldProps> = React.memo((props) => {
  const { datasheetElement } = props;

  const { 
    headingPlacedHorizontally, 
    headingReference, 
    headingSize, 
    headingPosition, 
    checked, // Get checked state from controller
    handleCheckboxFieldValueChange 
  } = useCheckboxFieldController(props);

  const isEditable = useDatasheetResultFieldEditable(Boolean(datasheetElement.original.editable));
  const forceDisabled = useForceDisabled();

  const { registerField, focusNext, focusPrev } = useDatasheetNavigationContext();
  const fieldContainerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!isEditable) return;
    registerField(datasheetElement.id, {
      focus: () => {
        const input = fieldContainerRef.current?.querySelector<HTMLInputElement>('input[type="checkbox"]');
        input?.focus();
      },
    });
    return () => { registerField(datasheetElement.id, null); };
  }, [datasheetElement.id, registerField, isEditable]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Tab' || !isEditable) return;
      e.preventDefault();
      if (e.shiftKey) {
        focusPrev(datasheetElement.id);
      } else {
        focusNext(datasheetElement.id);
      }
    },
    [datasheetElement.id, focusNext, focusPrev, isEditable],
  );

  const fieldWidth = datasheetElement.original.width;
  const fieldHeight = datasheetElement.original.height;

  const headingAlignmentStyle = getHeadingAlignmentStyle({
    headingPosition,
    headAlign: datasheetElement.original.headalign,
    fieldWidth,
  });

  const headingPositionOffset = getHeadingPositionOffset({
    headingPosition,
    headAlign: datasheetElement.original.headalign,
    headingSize,
    fieldWidth,
    fieldHeight,
  });

  const fieldIsEmpty = (() => { const v = (datasheetElement.original as any).field?.name ?? (datasheetElement.original as any).field?.value; return v == null || String(v).trim() === ''; })();

  const labelComponent = (
    <DatasheetItemLabel
      reference={headingReference}
      inline={headingPlacedHorizontally}
      withImg={datasheetElement.original.showfieldtype}
      withText={datasheetElement.original.headdisplay}
      text={datasheetElement.original.headtext}
      img={datasheetElement.original.field.imageURL}
      textCustomization={{
        fontSize: datasheetElement.original.headfontsize,
        fontFamily: datasheetElement.original.headfont,
        color: datasheetElement.original.headcolor,
        align: datasheetElement.original.headalign,
        fontStyles: datasheetElement.original.headstyle,
      }}
      absolutePosition={true}
      alignmentStyle={headingAlignmentStyle}
      positionOffset={headingPositionOffset}
    />
  );

  return (
    <DatasheetItem
      style={{
        top: datasheetElement.coordinates.y,
        left: datasheetElement.coordinates.x,
      }}
    >
      {labelComponent}
      <Flex alignItems="center" style={{ position: 'relative' }}>
        <FieldContainer
          ref={fieldContainerRef}
          fieldId={datasheetElement.original.field.fieldid ?? datasheetElement.original.field.id}
          onKeyDown={handleKeyDown}
          showBorder={datasheetElement.original.showborder}
          width={fieldWidth}
          height={fieldHeight}
          required={datasheetElement.original.required}
          isEmpty={fieldIsEmpty}
        >
          <Checkbox
            classes={{
              label: "DatasheetsResultCheckboxField__checkbox__label",
            }}
            checked={forceDisabled ? false : checked}
            onChange={isEditable ? handleCheckboxFieldValueChange : undefined}
            disabled={!isEditable}
          />
        </FieldContainer>
        {datasheetElement.original.helptext && (
          <Hint description={datasheetElement.original.helptext} />
        )}
      </Flex>
    </DatasheetItem>
  );
});
